<template>
  <section class="activity-stage beginning-letter">
    <div :class="{ isMobileView: isMobile }" class="images-holder">
      <img :src="currentStep.image" :alt="currentStep.imageName" />
    </div>
    <slot name="keyboard"></slot>
  </section>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'ObjectShuffle',
      sfx: null
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },

    async loadImages() {
      this.rounds[0].steps.forEach(({ image }) => this.queueImage(image));
      return this.downloadImages();
    },

    async loadSounds() {
      const [{ steps }] = this.rounds;

      const soundPaths = steps
        .filter(({ imageAudio }) => imageAudio)
        .map(({ imageAudio }) => imageAudio);

      this.queueAudio(soundPaths);

      const cache = await this.downloadAudio();
      return steps.map(({ imageAudio }) => {
        if (!imageAudio) {
          return null;
        }
        return cache.get(imageAudio);
      });
    },

    getKeyboardConfig() {
      const { characters } = this.activityData.keyboard;
      const isTouchHold = !!characters;
      const showTargets = this.isMobile && !(this.ipadWithoutExternalKeyboard && isTouchHold);

      return {
        showTargets
      };
    },

    getActivityState() {
      const { rounds } = this.activityData;
      const letters = this.activityData.keyboard.characters;
      const written = letters ? 'Hold the target. Type each letter.' : 'Type each letter.';
      const voice = letters
        ? `${this.voiceInstructionsPrefix}/shuffledletters-${this.getRandomVoice()}-1.mp3`
        : `${this.voiceInstructionsPrefix}/shuffledlettersnth-${this.getRandomVoice()}-1.mp3`;

      return {
        rounds,
        instructions: {
          written,
          voice,
          delay: 4
        }
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey() {
      this.disableKeyboard();
      this.sndType();
      this.handleStepEnd();
    },

    handleStepEnd() {
      const { stepIndex, imageAudio } = this.currentStep;
      if (imageAudio) {
        this.sfx[stepIndex].play();
        TweenMax.delayedCall(1.75, this.finishStep);
      } else {
        TweenMax.delayedCall(0.25, this.finishStep);
      }
    },

    finishStep() {
      if (this.isLastStepInRound) {
        this.handleRoundEnd();
      } else {
        this.completeStep();
        this.setCurrentStep();
        this.enableKeyboard();
      }
    },

    handleRoundEnd() {
      TweenMax.delayedCall(0.5, this.completeActivity);
    }
  }
};
</script>

<style lang="scss" scoped>
.images-holder {
  position: relative;
  top: 10px;
  z-index: 100;
  height: 420px;
  text-align: center;
  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
  }
}
.images-holder.isMobileView {
  height: 325px;
}
</style>
